import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Sparkles, X } from "lucide-react";
import { OPEN_EVENT, fetchHistory, getSessionId, renderRich, streamChat } from "@/lib/concierge";
import { EASE } from "@/lib/scroll";
import { track } from "@/lib/pixel";

const SUGGESTIONS = [
  "Which glitter suits brown eyes for a wedding?",
  "Mink vs faux mink lashes - which should I buy?",
  "How do I use the VelvetEdge blender?",
  "Best 3 brushes for a beginner?",
];

const Bubble = ({ m, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: EASE }}
    data-testid={`concierge-message-${m.role}-${i}`}
    className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
      m.role === "user" ? "self-end rounded-br-md bg-champagne text-ink" : "self-start rounded-bl-md border border-cream/10 bg-cream/[0.05] text-cream"
    }`}
  >
    {m.content ? renderRich(m.content) : <span className="typing" aria-label="Thinking"><i /><i /><i /></span>}
  </motion.div>
);

export const Concierge = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const loaded = useRef(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(null);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setError(null);
    setBusy(true);
    setMessages((ms) => [...ms, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      await streamChat(sessionId.current, message, (full) =>
        setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, content: full } : m))),
      );
    } catch (e) {
      setMessages((ms) => ms.slice(0, -1));
      setError(e.message.startsWith("concierge") ? "The concierge is unavailable right now - please try again shortly." : e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    sessionId.current = getSessionId();
    const onOpen = (e) => {
      setOpen(true);
      if (e.detail?.prompt) setTimeout(() => send(e.detail.prompt), 350);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    fetchHistory(sessionId.current)
      .then((h) => setMessages((ms) => (ms.length ? ms : h.map(({ role, content }) => ({ role, content })))))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 400);
    track("Contact", { content_name: "beauty-concierge" });
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            data-testid="concierge-open-button"
            aria-label="Ask the Beauty Concierge"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 12 }}
            transition={{ duration: 0.5, ease: EASE, delay: 1.2 }}
            className="concierge-fab fixed bottom-24 right-4 z-50 flex items-center gap-2 rounded-full border border-champagne/40 bg-ink/85 py-3 pl-4 pr-5 text-sm text-cream shadow-2xl backdrop-blur-xl sm:bottom-6 sm:right-6"
          >
            <Sparkles className="h-4 w-4 text-champagne" />
            <span className="hidden sm:inline">Ask the concierge</span>
            <span className="sm:hidden">Ask AI</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.section
            key="panel"
            data-testid="concierge-panel"
            role="dialog"
            aria-label="Beauty Concierge"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-x-3 bottom-3 top-[10svh] z-50 flex flex-col overflow-hidden rounded-3xl border border-champagne/25 bg-ink/95 text-cream shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)] backdrop-blur-2xl sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[640px] sm:max-h-[calc(100svh-48px)] sm:w-[400px]"
            data-lenis-prevent
          >
            <header className="flex items-center justify-between border-b border-cream/10 px-5 py-4">
              <div>
                <p className="flex items-center gap-2 font-display text-xl text-cream"><Sparkles className="h-4 w-4 text-champagne" /> Beauty Concierge</p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-stone">AI stylist · powered by ChatGPT</p>
              </div>
              <button data-testid="concierge-close-button" aria-label="Close" onClick={() => setOpen(false)} className="icon-btn"><X className="h-4 w-4" /></button>
            </header>

            <div ref={listRef} data-testid="concierge-messages" className="no-scrollbar flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-5">
              {messages.length === 0 && (
                <div className="my-auto">
                  <p className="font-display text-2xl leading-snug text-cream">Tell me your eye colour, the occasion, or what you are stuck on - I will pick from the Beauty Edit for you.</p>
                  <div className="mt-6 flex flex-col gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={s} data-testid={`concierge-suggestion-${i}`} onClick={() => send(s)} className="rounded-2xl border border-cream/10 px-4 py-3 text-left text-sm text-cream/90 transition-colors hover:border-champagne/60 hover:text-champagne">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
              {error && <p data-testid="concierge-error" className="self-start rounded-2xl border border-red-300/30 bg-red-300/10 px-4 py-2 text-xs text-red-200">{error}</p>}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-cream/10 p-3"
            >
              <input
                ref={inputRef}
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={600}
                placeholder="Ask about shades, lashes, brushes…"
                className="h-12 flex-1 rounded-full border border-cream/15 bg-transparent px-5 text-sm text-cream placeholder:text-stone focus:border-champagne focus:outline-none"
              />
              <button data-testid="concierge-send-button" type="submit" disabled={busy || !input.trim()} aria-label="Send" className="grid h-12 w-12 place-items-center rounded-full bg-champagne text-ink transition-opacity disabled:opacity-40">
                <ArrowUp className="h-4 w-4" />
              </button>
            </form>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};
